<template>
  <div class="embed-payment-container">
    <vue-element-loading
      :active="loading"
      is-full-screen
    />

    <div :class="['embed-wrapper', { 'mobile': isMobile }]">
      <!-- Missing username warning -->
      <a-alert
        v-if="!paymentUsername"
        type="warning"
        :message="$t('EMBED_MISSING_USERNAME')"
        show-icon
        class="embed-missing-alert"
      />

      <!-- Embed Payment Info Banner -->
      <div v-if="embedServiceData" class="embed-info-banner">
        <div class="embed-info-row">
          <a-icon type="user" class="embed-info-icon" />
          <span class="embed-info-label">{{ $t('EMBED_PAYMENT_ACCOUNT') }}</span>
          <span class="embed-info-value">{{ paymentUsername }}</span>
        </div>
        <div class="embed-info-row">
          <a-icon type="gift" class="embed-info-icon" />
          <span class="embed-info-label">{{ $t('EMBED_PAYMENT_PACKAGE') }}</span>
          <span class="embed-info-value">{{ embedServiceData.title }}</span>
        </div>
        <div class="embed-info-row embed-info-total">
          <a-icon type="dollar" class="embed-info-icon" />
          <span class="embed-info-label">{{ $t('EMBED_PAYMENT_TOTAL') }}</span>
          <span class="embed-info-value embed-total-value">
            {{ embedServiceData.price | addCommas }} {{ currency }}
          </span>
        </div>
      </div>

      <div class="steps-action">
        <a-button
          v-if="current > 0 && current < steps.length - 1"
          class="action-btn"
          @click="prev"
        >
          {{ $t("PREVIOUS_STEP") }}
        </a-button>
        <a-button
          v-if="current < steps.length - 2"
          type="primary"
          class="action-btn action-btn-primary"
          @click="next"
        >
          {{ $t("NEXT_STEP") }}
        </a-button>
        <a-button
          v-if="current == steps.length - 2"
          type="primary"
          class="action-btn btn-success"
          @click="confirmPayment"
        >
          {{ $t("FINISH_STEP") }}
        </a-button>
      </div>

      <div class="steps-content">
        <!-- Step Service (username locked when provided via URL) -->
        <service-step
          v-if="steps && steps[current] && steps[current].id === 'service'"
          :payment-username="paymentUsername"
          :trigger-username="triggerUsername"
          :active-service="activeService"
          :payment-service-id="paymentServiceId"
          :option="option"
          :currency="currency"
          :disabled-username="hasInitialUsername"
          :hide-username="hasInitialUsername"
          @username-change="handleUsernameChange"
          @service-tab-change="handleServiceTabChange"
          @service-change="handleServiceChange"
          @service-click="handleServiceClick"
        />

        <!-- Step Method-->
        <method-step
          v-if="steps && steps[current] && steps[current].id === 'method'"
          :payment-method-id="paymentMethodId"
          :option="option"
          @method-change="handleMethodChange"
          @method-click="handleMethodClick"
        />

        <!-- Step Payment-->
        <payment-step
          v-if="steps && steps[current] && steps[current].id === 'payment'"
          :payment-qr-code="paymentQrCode"
          :payment="payment"
          :payment-method-text="paymentMethodText"
          :payment-method-id="paymentMethodId"
          :payment-total="paymentTotal"
          :payment-note="paymentNote"
          :payment-pre-note="paymentPreNote"
          @copy="handleCopy"
          @confirm-payment="confirmPayment"
        />

        <!-- Step Finish-->
        <finish-step
          v-if="steps && steps[current] && steps[current].id === 'finish'"
          :payment="payment"
          :payment-success="paymentSuccess"
        />
      </div>
    </div>

    <payment-modals
      :payment-process="paymentProcess"
      :confirm-box="confirmBox"
      :payment-total="paymentTotal"
      :payment-method-name="paymentMethodName"
      :payment="payment"
      :payment-note="paymentNote"
      :payment-finish="paymentFinish"
      :payment-contact="paymentContact"
      @close-process="handleCloseProcess"
      @confirm-ok="processConfirmPayment"
      @confirm-cancel="handleConfirmCancel"
    />
  </div>
</template>

<script>
import mixins from './mixins';
import ServiceStep from '../components/ServiceStep.vue';
import MethodStep from '../components/MethodStep.vue';
import PaymentStep from '../components/PaymentStep.vue';
import FinishStep from '../components/FinishStep.vue';
import PaymentModals from '../components/PaymentModals.vue';

export default {
  name: 'EmbedPayment',
  mixins: [mixins],
  data() {
    return {
      hasInitialUsername: Boolean(this.$route.query.username),
    };
  },
  computed: {
    embedServiceData() {
      if (!this.paymentUsername || !this.payment?.service?.id) {
        return null;
      }
      return this.findServiceData;
    },
  },
  components: {
    'service-step': ServiceStep,
    'method-step': MethodStep,
    'payment-step': PaymentStep,
    'finish-step': FinishStep,
    'payment-modals': PaymentModals,
  },
  methods: {
    handlePopupNotification() {
      // Embed mode: suppress promo/header notification popup
    },
    handleUsernameChange(value) {
      this.paymentUsername = value;
      this.onChangeUsername();
    },
    handleServiceTabChange(key) {
      this.activeService = key;
    },
    handleServiceChange(value) {
      this.paymentServiceId = value;
    },
    handleServiceClick(item) {
      this.onChangeService(item);
    },
    handleMethodChange(value) {
      this.paymentMethodId = value;
    },
    handleMethodClick(item) {
      this.onChangeMethod(item);
    },
    handleCopy(content) {
      this.copyToClipboard(content);
    },
    handleCloseProcess() {
      this.$store.commit('payment/SET_PAYMENT_PROCESS', { show: false });
    },
    handleConfirmCancel() {
      this.$store.commit('payment/SET_CONFIRM_BOX', false);
    },
  },
};
</script>

<style scoped>
.embed-payment-container {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: var(--spacing-md);
}

.embed-wrapper {
  width: 100%;
}

.embed-wrapper.mobile {
  padding: 0;
}

.embed-missing-alert {
  margin-bottom: var(--spacing-md);
  border-radius: var(--border-radius-md);
}

/* Embed Info Banner */
.embed-info-banner {
  background: linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%);
  border: 1px solid #91d5ff;
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md) var(--spacing-lg);
  margin-bottom: var(--spacing-md);
}

.embed-info-row {
  display: flex;
  align-items: center;
  padding: var(--spacing-xs) 0;
}

.embed-info-row + .embed-info-row {
  border-top: 1px solid rgba(145, 213, 255, 0.3);
}

.embed-info-icon {
  font-size: 16px;
  color: #1890ff;
  margin-right: var(--spacing-sm);
  width: 20px;
  text-align: center;
}

.embed-info-label {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  min-width: 100px;
}

.embed-info-value {
  color: var(--text-primary);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
  margin-left: auto;
}

.embed-info-total {
  padding-top: var(--spacing-sm);
}

.embed-total-value {
  color: var(--primary-color);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}

@media (max-width: 480px) {
  .embed-info-banner {
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .embed-info-label {
    min-width: 80px;
  }
}

.steps-action {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-xs);
  margin-bottom: var(--spacing-md);
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .steps-action {
    justify-content: center;
  }
}

.action-btn {
  min-width: 100px;
  height: 36px;
  border-radius: var(--border-radius-md);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
  transition: all var(--transition-base);
  touch-action: manipulation;
}

@media (max-width: 768px) {
  .action-btn {
    min-width: 90px;
    height: 44px;
    font-size: var(--font-size-base);
    padding: 0 var(--spacing-md);
  }
}

.btn-success {
  background-color: var(--success-color) !important;
  border-color: var(--success-color) !important;
}

.btn-success:hover {
  background-color: var(--success-hover) !important;
  border-color: var(--success-hover) !important;
}

.steps-content {
  margin-top: var(--spacing-md);
}

@media (max-width: 768px) {
  .embed-payment-container {
    padding: var(--spacing-sm);
  }

  .steps-action {
    text-align: center;
    margin-bottom: var(--spacing-md);
    gap: var(--spacing-sm);
  }

  .steps-content {
    margin-top: var(--spacing-sm);
  }
}

@media (max-width: 480px) {
  .embed-payment-container {
    padding: var(--spacing-xs);
  }

  .steps-action {
    flex-direction: column;
    width: 100%;
  }

  .action-btn {
    width: 100%;
    min-width: 100%;
  }
}
</style>
